(function () {
  'use strict';
  function uncoveredFraction(el, bounds, hitTest) {
    let uncovered = 0;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const hit = hitTest(bounds.left + bounds.width * (col + 0.5) / 3, bounds.top + bounds.height * (row + 0.5) / 3);
        if (hit && el.contains(hit)) uncovered++;
      }
    }
    return uncovered / 9;
  }
  function createTimer(emit, uuid) {
    let previous = null, exposures = new Map(), pageMs = 0, pageSeq = 0;
    function flush(reason) {
      exposures.forEach(x => {
        if (x.ms > 0) {
          emit('section_engagement', { ...x.info, exposure_id: x.id, active_ms: Math.round(x.ms), flush_seq: ++x.seq, flush_reason: reason });
          x.ms = 0;
        }
      });
      if (pageMs > 0) {
        emit('page_engagement_summary', { active_ms: Math.round(pageMs), flush_seq: ++pageSeq, flush_reason: reason, last_section_id: previous?.winner || 'unknown' });
        pageMs = 0;
      }
    }
    function reset() { exposures.clear(); pageMs = 0; previous = null; }
    function update(now, state, reason) {
      if (!state.allowed) { reset(); return; }
      if (previous?.allowed && previous.active) {
        const dt = Math.max(0, Math.min(now, previous.activeUntil) - previous.now);
        pageMs += dt;
        exposures.forEach((x, id) => {
          x.continuous += dt;
          if (previous.winner === id) x.ms += dt;
          if (!x.viewed && x.continuous >= 1000) {
            x.viewed = true;
            emit('section_view', { ...x.info, exposure_id: x.id });
          }
        });
      }
      const visible = new Map(state.active ? state.sections.map(x => [x.section_id, x]) : []);
      const ended = [...exposures.keys()].some(id => !visible.has(id));
      if (ended || reason || (previous && state.winner !== previous.winner)) flush(reason || 'section_change');
      exposures.forEach((x, id) => { if (!visible.has(id)) exposures.delete(id); });
      visible.forEach((info, id) => {
        if (!exposures.has(id)) exposures.set(id, { info, id: uuid(), ms: 0, seq: 0, continuous: 0, viewed: false });
      });
      previous = { ...state, now };
    }
    return { update, flush, reset };
  }
  function createGalleryTimer(emit, uuid) {
    let previous = null, signature = '', setId = '', members = [], exposures = new Map(), qualified = new Set(), completed = false, membershipAnnounced = false;
    function reset() { previous = null; signature = ''; setId = ''; members = []; exposures.clear(); qualified.clear(); completed = false; membershipAnnounced = false; }
    function flush(x, reason, end) {
      if (x.ms > 0 || end) {
        emit('product_image_engagement', { ...x.info, gallery_set_id: setId, gallery_size: members.length, exposure_id: x.id, active_ms: Math.round(x.ms), exposure_ms: Math.round(x.continuous), flush_seq: ++x.seq, exposure_end: end, qualified_view: x.continuous >= 1000, flush_reason: reason });
        x.ms = 0;
      }
    }
    function update(now, state, reason) {
      if (!state.allowed) { reset(); return; }
      if (previous?.active) {
        const dt = Math.max(0, Math.min(now, previous.activeUntil) - previous.now);
        exposures.forEach((x, id) => {
          x.continuous += dt;
          if (id === previous.winner) x.ms += dt;
          if (x.continuous >= 1000) qualified.add(id);
        });
        if (!completed && members.length && members.every(x => qualified.has(x.media_id))) {
          completed = true;
          emit('gallery_complete', { product_id: members[0].product_id, gallery_set_id: setId, gallery_size: members.length });
        }
      }
      const nextSignature = state.members.map(x => x.media_id).join(',');
      const changed = nextSignature !== signature;
      const visible = new Map(state.active ? state.visible.map(x => [x.media_id, x]) : []);
      exposures.forEach((x, id) => {
        const end = changed || !visible.has(id) || visible.get(id).view_mode !== x.info.view_mode;
        if (end || reason || previous?.winner !== state.winner) flush(x, changed ? 'gallery_change' : reason || 'image_change', end);
        if (end) exposures.delete(id);
      });
      if (changed) { signature = nextSignature; setId = uuid(); members = state.members; qualified.clear(); completed = false; membershipAnnounced = false; }
      // Eligibility is a gallery opportunity, not a viewport exposure. Announce
      // once on active consented sampling, before any exposure in the same set.
      if (!membershipAnnounced && state.active && members.length) {
        members.forEach(info => emit('gallery_image_available', {...info, gallery_set_id:setId, gallery_size:members.length}));
        membershipAnnounced = true;
      }
      visible.forEach((info, id) => {
        if (!members.some(x => x.media_id === id) || exposures.has(id)) return;
        const x = {info, id: uuid(), ms: 0, continuous: 0, seq: 0};
        exposures.set(id, x);
        emit('product_image_exposure', { ...info, gallery_set_id: setId, gallery_size: members.length, exposure_id: x.id });
      });
      previous = { ...state, now };
      return { gallerySize: members.length, qualifiedImages: qualified.size, complete: completed, setId };
    }
    return {update, reset};
  }
  function createDrawerTracker(emit) {
    let previousOpen = null, userIntentAt = -Infinity;
    function intent(now, permitted) { if (permitted) userIntentAt = now; }
    function update(now, state) {
      if (!state.allowed) userIntentAt = -Infinity;
      if (previousOpen === false && state.open && state.allowed && state.visible) {
        emit('cart_drawer_open', { open_reason: now - userIntentAt <= 2000 ? 'user' : 'unknown', cart_surface: 'drawer' });
        userIntentAt = -Infinity;
      }
      previousOpen = state.open;
    }
    return {intent, update};
  }
  function createBehaviorTransport({ getGtag, isAllowed, onStatus, maxQueue = 200 }) {
    const pending = [];
    let dropped = 0;
    function status(value) { onStatus(value, pending.length, dropped); }
    function flush() {
      if (!isAllowed()) { pending.length = 0; status('analytics_unavailable'); return; }
      if (!pending.length) return;
      const gtag = getGtag();
      if (typeof gtag !== 'function') { status('waiting_for_existing_gtag'); return; }
      while (pending.length) {
        if (!isAllowed()) { pending.length = 0; status('analytics_unavailable'); return; }
        const next = pending[0];
        try { gtag('event', next.name, next.parameters); }
        catch (_) { status('existing_gtag_error'); return; }
        pending.shift();
      }
      status(dropped ? 'event_submitted_with_queue_loss' : 'event_submitted');
    }
    function push(name, parameters) {
      if (!isAllowed()) { pending.length = 0; status('analytics_unavailable'); return; }
      // Retry earlier records before admitting another. The existing tag owns delivery.
      flush();
      if (pending.length >= maxQueue) { dropped++; status('queue_overflow'); return; }
      pending.push({name, parameters});
      flush();
    }
    return {push, flush};
  }
  function sectionRoots(doc) {
    const roots = [...doc.querySelectorAll('main .shopify-section')];
    if (roots.length) return roots;
    const main = doc.querySelector('main');
    return main ? [main] : [];
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { createTimer, createGalleryTimer, createDrawerTracker, uncoveredFraction, sectionRoots, createBehaviorTransport }; return; }
  if (window.zitunBehaviorStarted) return;
  const configElement = document.getElementById('zitun-behavior-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch (_) { return; }
  window.zitunBehaviorStarted = true;
  if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], () => {});
  }
  const uuid = () => crypto.randomUUID();
  const pageId = uuid();
  let seq = 0, lastActivity = performance.now(), lastY = window.scrollY;
  let direction = null, extremeY = lastY, lastReversal = -Infinity, lastSection = 'unknown';
  let previouslyAllowed = false, stopped = false;
  const sectionKeys = config.section_keys || {};
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch (_) { return false; }
  };
  const transport = createBehaviorTransport({
    getGtag: () => config.transport === 'existing_gtag' ? window.gtag : null,
    isAllowed: allowed,
    onStatus: (status, pending, dropped) => {
      configElement.dataset.transportStatus = status;
      configElement.dataset.pendingEvents = String(pending);
      configElement.dataset.droppedEvents = String(dropped);
    }
  });
  function send(name, fields) {
    if (!allowed()) return;
    const payload = { schema_version: 1, event_id: uuid(), page_view_id: pageId, event_seq: ++seq, client_timestamp_ms: Date.now(), page_type: config.page_type, page_path: config.page_path, ...fields };
    if (config.preview) {
      configElement.dataset.eventSeq = String(seq);
      configElement.dataset.lastEvent = name;
      configElement.dataset.lastDuration = String(fields.active_ms || 0);
      configElement.dataset.lastReason = fields.flush_reason || '';
    }
    if (config.preview) console.info('Zitun behavior', JSON.stringify({ event: name, ...payload }));
    const {event_id, page_path, ...parameters} = payload;
    transport.push(name, { ...parameters, behavior_event_id: event_id, storefront_path: page_path, send_to: config.measurement_id, ...(config.preview ? { debug_mode: true } : {}) });
  }
  const timer = createTimer(send, uuid);
  const galleryTimer = createGalleryTimer(send, uuid);
  const drawerTracker = createDrawerTracker(send);
  let galleryMethod = 'initial', galleryMethodAt = performance.now(), touchOrigin = null;
  function layoutAvailable(el) {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || node.hidden) return false;
    }
    return true;
  }
  function imageVisibility(el) {
    const images = [...el.querySelectorAll('img')].filter(img => img.complete && img.naturalWidth > 0 && layoutAvailable(img) && Number(getComputedStyle(img).opacity) > 0);
    let area = 0;
    images.forEach(img => {
      const r = img.getBoundingClientRect(), left = Math.max(r.left, 0), top = Math.max(r.top, 0);
      const w = Math.max(0, Math.min(r.right, innerWidth) - left), h = Math.max(0, Math.min(r.bottom, innerHeight) - top);
      if (!w || !h || !r.width || !r.height) return;
      const uncovered = uncoveredFraction(el, {left,top,width:w,height:h}, (x,y) => document.elementFromPoint(x,y));
      if (w * h * uncovered / (Math.min(r.width,innerWidth) * Math.min(r.height,innerHeight)) >= 0.5) area = Math.max(area, w*h*uncovered);
    });
    return area;
  }
  function gallery() {
    const root = document.querySelector('main [id^="product-media-"]');
    if (config.page_type !== 'product' || !root || !config.product_id) return {members:[],visible:[],winner:null};
    const sectionId = root.id.slice('product-media-'.length);
    const metadata = new Map((config.media || []).filter(x => x.type === 'image').map(x => [String(x.id), x]));
    const nodes = [...root.querySelectorAll('[data-type="image"][data-media-id]')].filter(el => !el.closest('.splide__slide--clone') && !el.closest('[id^="ProductModal-"]') && layoutAvailable(el) && (!el.classList.contains('media-slide') || el.classList.contains('splide__slide')));
    const members = [], candidates = [];
    nodes.forEach(el => {
      const id = el.getAttribute('data-media-id').split('-').pop();
      if (!metadata.has(id)) return;
      let info = members.find(x => x.media_id === id);
      if (!info) { info = {product_id:config.product_id,media_id:id,image_index:members.length+1}; members.push(info); }
      candidates.push({el,info,mode:'inline'});
    });
    const modal = document.getElementById('ProductModal-' + sectionId);
    if (modal && layoutAvailable(modal)) {
      modal.querySelectorAll('[x-slide-index], [id*="-image-zoom-"]').forEach(el => {
        if (el.closest('.splide__slide--clone')) return;
        const position = Number(el.getAttribute('x-slide-index') || el.id.split('-image-zoom-')[0]);
        const media = [...metadata.values()].find(x => x.position === position);
        const info = media && members.find(x => x.media_id === String(media.id));
        if (info) candidates.push({el,info,mode:'zoom'});
      });
    }
    const visible = new Map();
    candidates.forEach(({el,info,mode}) => {
      const area = imageVisibility(el);
      if (!area) return;
      const previous = visible.get(info.media_id);
      if (!previous || area > previous.area) visible.set(info.media_id, {area,info:{...info,view_mode:mode,interaction_method:performance.now()-galleryMethodAt<5000 ? galleryMethod : 'unknown'}});
    });
    const sorted = [...visible.values()].sort((a,b)=>b.area-a.area || a.info.image_index-b.info.image_index);
    return {members, visible:sorted.map(x=>x.info), winner:sorted[0]?.info.media_id || null};
  }
  function sections() {
    return sectionRoots(document).map((el, i) => {
      const r = el.getBoundingClientRect();
      const w = Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0));
      const h = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
      const possible = r.height > 0 && r.width > 0 && w > 0 && h / Math.min(r.height, innerHeight) >= 0.5 && getComputedStyle(el).visibility !== 'hidden';
      const uncovered = possible ? uncoveredFraction(el, {left:Math.max(r.left,0),top:Math.max(r.top,0),width:w,height:h}, (x,y) => document.elementFromPoint(x,y)) : 0;
      const visible = possible && h * uncovered / Math.min(r.height, innerHeight) >= 0.5;
      const rawId = el.matches('main') ? 'main_content' : el.id.replace(/^shopify-section-/, '');
      const suffix = rawId.includes('__') ? rawId.split('__').slice(1).join('__') : rawId;
      const defaultKey = suffix === 'main_content' ? 'page_content' : suffix === 'main' ? (config.page_type === 'product' ? 'product_detail' : config.page_type === 'collection' ? 'product_list' : 'page_content') : suffix === 'banner' ? 'page_banner' : 'other';
      return { visible, area: w * h * uncovered, center: Math.abs((r.top + r.bottom) / 2 - innerHeight / 2), info: { section_id: rawId, section_key: sectionKeys[suffix] || defaultKey, section_index: i + 1 } };
    }).filter(x => x.visible).sort((a, b) => b.area - a.area || a.center - b.center || a.info.section_index - b.info.section_index);
  }
  function sample(reason) {
    const now = performance.now();
    const permitted = allowed();
    transport.flush();
    const drawer = document.getElementById('CartDrawer');
    const drawerDialog = drawer?.querySelector('[role="dialog"]');
    const drawerBounds = drawerDialog?.getBoundingClientRect();
    const drawerOpen = !!(drawer && drawerDialog && drawer.classList.contains('cart-open') && layoutAvailable(drawerDialog) && drawerBounds.width > 0 && drawerBounds.height > 0 && drawerBounds.right > 0 && drawerBounds.bottom > 0 && drawerBounds.left < innerWidth && drawerBounds.top < innerHeight);
    drawerTracker.update(now, {allowed:permitted,visible:!stopped && document.visibilityState === 'visible',open:drawerOpen});
    if (config.preview) configElement.dataset.cartDrawerStatus = drawerOpen ? 'open' : 'closed';
    const active = permitted && !stopped && document.visibilityState === 'visible' && now < lastActivity + 30000;
    const visible = active ? sections() : [];
    const winner = visible[0]?.info.section_id || null;
    timer.update(now, { allowed: permitted, active, activeUntil: lastActivity + 30000, sections: visible.map(x => x.info), winner }, reason);
    const images = permitted ? gallery() : {members:[],visible:[],winner:null};
    const galleryState = galleryTimer.update(now, {allowed:permitted,active,activeUntil:lastActivity+30000,...images}, reason);
    if (config.preview) {
      configElement.dataset.currentMedia = active ? images.winner || 'none' : 'none';
      configElement.dataset.gallerySize = String(galleryState?.gallerySize || 0);
      configElement.dataset.qualifiedImages = String(galleryState?.qualifiedImages || 0);
      configElement.dataset.galleryComplete = String(galleryState?.complete || false);
    }
    if (!permitted && previouslyAllowed) { direction = null; extremeY = window.scrollY; }
    previouslyAllowed = permitted;
    if (winner) lastSection = winner;
    configElement.dataset.consentStatus = permitted ? 'analytics_allowed' : 'analytics_unavailable';
    configElement.dataset.activeStatus = active ? 'active' : 'paused';
    if (config.preview) configElement.dataset.visibilityStatus = document.visibilityState;
    if (config.preview) configElement.dataset.currentSection = winner || 'none';
  }
  let lastMove = -Infinity;
  function activity() { sample(); lastActivity = performance.now(); sample(); }
  document.addEventListener('pointerdown', activity, { passive: true });
  document.addEventListener('pointermove', () => { if (performance.now() - lastMove >= 100) { lastMove = performance.now(); activity(); } }, { passive: true });
  document.addEventListener('keydown', activity, { passive: true });
  document.addEventListener('click', event => {
    if (event.isTrusted && event.target.closest?.('[aria-controls="CartDrawer"]')) drawerTracker.intent(performance.now(), allowed() && document.visibilityState === 'visible');
    const target = event.target.closest?.('main [id^="product-media-"], [id^="ProductModal-"]');
    if (!target) return;
    sample();
    const control = event.target.closest('.x-thumbnail,.splide__arrow,[aria-label="Image zoom"]');
    galleryMethod = control?.classList.contains('x-thumbnail') ? 'thumbnail' : control?.classList.contains('splide__arrow--next') ? 'next' : control?.classList.contains('splide__arrow--prev') ? 'previous' : control?.getAttribute('aria-label') === 'Image zoom' ? 'zoom' : 'unknown';
    galleryMethodAt = performance.now();
    requestAnimationFrame(() => sample('gallery_interaction'));
  }, {capture:true,passive:true});
  document.addEventListener('touchstart', event => {
    if (event.target.closest?.('main [id^="product-media-"]')) touchOrigin = {x:event.touches[0]?.clientX,y:event.touches[0]?.clientY};
  }, {passive:true});
  document.addEventListener('touchend', event => {
    const point = event.changedTouches[0];
    if (touchOrigin && point && Math.abs(point.clientX-touchOrigin.x)>30 && Math.abs(point.clientX-touchOrigin.x)>Math.abs(point.clientY-touchOrigin.y)) { galleryMethod='swipe'; galleryMethodAt=performance.now(); sample('gallery_interaction'); }
    touchOrigin=null;
  }, {passive:true});
  document.addEventListener('eurus:variant:change', () => { galleryMethod='variant'; galleryMethodAt=performance.now(); requestAnimationFrame(() => sample('variant_change')); });
  document.addEventListener('touchstart', activity, { passive: true });
  window.addEventListener('scroll', () => {
    const from = lastSection;
    activity();
    const y = window.scrollY, delta = y - lastY, now = performance.now();
    const next = delta > 0 ? 'down' : delta < 0 ? 'up' : direction;
    if (allowed() && document.visibilityState === 'visible' && next) {
      if (!direction) { direction = next; extremeY = y; }
      if ((direction === 'down' && y > extremeY) || (direction === 'up' && y < extremeY)) extremeY = y;
      if (next !== direction && Math.abs(y - extremeY) >= 100 && now - lastReversal >= 1000) {
        sample('scroll_reversal');
        direction = next; extremeY = y; lastReversal = now;
        send('scroll_direction_change', { direction, from_section_id: from, to_section_id: lastSection });
      }
    }
    lastY = y;
  }, { passive: true });
  document.addEventListener('visitorConsentCollected', () => { sample('consent_change'); });
  document.addEventListener('visibilitychange', () => { sample(document.visibilityState === 'hidden' ? 'hidden' : 'visible'); });
  window.addEventListener('pagehide', () => { stopped = true; sample('pagehide'); });
  window.addEventListener('pageshow', () => { stopped = false; lastActivity = performance.now(); sample(); });
  setInterval(() => sample(), 250);
  setInterval(() => sample('periodic'), 10000);
  if (config.preview && new URLSearchParams(location.search).get('zitun_tracking_test') === '1') {
    const panel = document.createElement('details');
    panel.id = 'zitun-tracking-test';
    panel.style.cssText = 'position:fixed;bottom:70px;right:8px;z-index:2147483646;background:white;color:black;border:1px solid black;padding:8px;max-width:280px;font:14px sans-serif';
    const summary = document.createElement('summary');
    summary.textContent = 'Preview tracking test';
    panel.append(summary);
    const note = document.createElement('p');
    note.textContent = 'Test browser only. Controls change one consent purpose. Marketing tests require an existing yes/no choice so it can be restored.';
    panel.append(note);
    [false, true].forEach(value => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = value ? 'Allow analytics for test' : 'Decline analytics for test';
      button.style.cssText = 'display:block;padding:8px;margin:4px;border:1px solid black';
      button.addEventListener('click', () => {
        const privacy = window.Shopify?.customerPrivacy;
        if (typeof privacy?.setTrackingConsent !== 'function') { note.textContent = 'Privacy API unavailable. No consent changed.'; return; }
        privacy.setTrackingConsent({analytics:value}, () => { sample('consent_change'); });
      });
      panel.append(button);
    });
    ['marketing'].forEach(purpose => {
      [false, true].forEach(value => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = value ? 'Allow marketing for test' : 'Decline marketing for test';
        button.style.cssText = 'display:block;padding:8px;margin:4px;border:1px solid black';
        button.addEventListener('click', () => {
          const privacy = window.Shopify?.customerPrivacy;
          const current = privacy?.currentVisitorConsent?.()?.[purpose];
          if (typeof privacy?.setTrackingConsent !== 'function' || !['yes','no'].includes(current)) {
            note.textContent = 'Marketing choice is unset or unavailable. No consent changed; an unset choice cannot be restored with these test controls.';
            return;
          }
          privacy.setTrackingConsent({[purpose]:value}, () => { sample('consent_change'); });
        });
        panel.append(button);
      });
    });
    const status = document.createElement('output');
    panel.append(status);
    setInterval(() => {
      const privacy = window.Shopify?.customerPrivacy;
      const choice = privacy?.currentVisitorConsent?.() || {};
      for (const purpose of ['analytics','marketing','preferences','sale_of_data']) configElement.dataset[purpose.replace(/_([a-z])/g,(_,c)=>c.toUpperCase())+'Choice'] = ['yes','no'].includes(choice[purpose]) ? choice[purpose] : 'unset_or_unavailable';
      const marketingAllowed = privacy?.marketingAllowed?.();
      configElement.dataset.marketingAllowed = typeof marketingAllowed === 'boolean' ? String(marketingAllowed) : 'unavailable';
      status.textContent = `${configElement.dataset.consentStatus}; ${configElement.dataset.activeStatus}; ${configElement.dataset.visibilityStatus}; events ${seq}; image ${configElement.dataset.currentMedia}; viewed ${configElement.dataset.qualifiedImages}/${configElement.dataset.gallerySize}; complete ${configElement.dataset.galleryComplete}; registered marketing ${configElement.dataset.marketingChoice}; marketing allowed ${configElement.dataset.marketingAllowed}`;
    }, 250);
    document.body.append(panel);
  }
  sample();
})();
